import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useConfigValue } from 'hooks/configs/use-config-value';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode, ServiceModeStatus } from 'state/service-mode/types';
import { StoreProxy } from 'state/store/hooks';
import {
  getDateNowByTimezone,
  isMobileOrderingAvailable,
  isRestaurantOpen,
  useGetRestaurantAvailabilityFn,
  useGetRestaurantFn,
} from 'utils/restaurant';
import { PICKUP_SERVICE_MODES, isCatering } from 'utils/service-mode';

import { IS_AVAILABLE_CURBSIDE_SERVICE_MODE } from './constants';

export const useServiceModeStatusGenerator = (ignoreFutureOrdering = false) => {
  const isCateringDeliveryEnabled = useFlag(LaunchDarklyFlag.ENABLE_CATERING_DELIVERY);
  const isCateringEnabled = useFlag(LaunchDarklyFlag.ENABLE_CATERING);
  const isCurbsideEnabled = useFlag(LaunchDarklyFlag.ENABLE_CURBSIDE);
  const isDeliveryEnabled = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  // Move these up in order to check for null (meaning LD service is down) or undefined
  // FYI - use ENABLE_* flags to avoid this necessity as null values will correctly disable the option
  const driveThruDisabled = useFlag(LaunchDarklyFlag.DISABLE_DRIVE_THRU);
  const dineInDisabled = useFlag(LaunchDarklyFlag.DISABLE_DINE_IN);
  const takeOutDisabled = useFlag(LaunchDarklyFlag.DISABLE_TAKE_OUT);
  const enableFutureOrdering = useFlag(LaunchDarklyFlag.ENABLE_FUTURE_ORDERING);
  const pickupWindowDisabled = useFlag(LaunchDarklyFlag.DISABLE_PICKUP_WINDOW);

  // ENABLE_DELIVERY_CHECKOUT_OUTSIDE_OPENING_HOURS
  const enableDeliveryOutsideOpeningHours = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_CHECKOUT_OUTSIDE_OPENING_HOURS
  );

  const generateServiceModeStatusForStore = useCallback(
    (store: StoreProxy | IRestaurantNode) => {
      const serviceModeStatus: ServiceModeStatus = {
        CATERING_DELIVERY: {
          capable: !!store.hasDelivery && !!store.hasCatering,
          available: false,
          disabled: !isCateringDeliveryEnabled,
        },
        CATERING_PICKUP: {
          capable: !!store.hasCatering,
          available: false,
          disabled: !isCateringEnabled,
        },
        CURBSIDE: {
          capable: !!store.hasCurbside,
          available: false,
          disabled: !isCurbsideEnabled,
          open: false,
        },
        DELIVERY: {
          capable: !!store.hasDelivery,
          available: false,
          disabled: !isDeliveryEnabled,
          open: false,
        },
        DRIVE_THRU: {
          capable: !!store.hasDriveThru,
          available: false,
          disabled: isNil(driveThruDisabled) || driveThruDisabled,
          open: false,
        },
        EAT_IN: {
          capable: !!store.hasDineIn,
          available: false,
          disabled: isNil(dineInDisabled) || dineInDisabled,
          open: false,
        },
        TAKEOUT: {
          capable: !!store.hasTakeOut,
          available: false,
          disabled: isNil(takeOutDisabled) || takeOutDisabled,
          open: false,
        },
        PICKUP_WINDOW: {
          capable: !!store.hasPickupWindow,
          available: false,
          disabled: pickupWindowDisabled,
          open: false,
        },
      };

      const dateNow = getDateNowByTimezone({
        timeZone: store.timezone || undefined,
      });

      const curbsideOpen = isRestaurantOpen(store.curbsideHours, dateNow);
      const deliveryOpen =
        isRestaurantOpen(store.deliveryHours, dateNow) || enableDeliveryOutsideOpeningHours;
      const diningRoomOpen = isRestaurantOpen(store.diningRoomHours, dateNow);
      const driveThruOpen = isRestaurantOpen(store.driveThruHours, dateNow);
      const pickupWindowOpen = isRestaurantOpen(store.pickupWindowHours, dateNow);

      /** Need to know if service mode is open for store selection 2.0 */
      serviceModeStatus.DELIVERY.open = deliveryOpen;
      serviceModeStatus.EAT_IN.open = diningRoomOpen;
      serviceModeStatus.TAKEOUT.open = diningRoomOpen;
      serviceModeStatus.CURBSIDE.open = curbsideOpen;
      serviceModeStatus.DRIVE_THRU.open = driveThruOpen;
      serviceModeStatus.PICKUP_WINDOW.open = pickupWindowOpen;

      if (enableFutureOrdering && !ignoreFutureOrdering) {
        serviceModeStatus.CATERING_DELIVERY.available = serviceModeStatus.CATERING_DELIVERY.capable;
        serviceModeStatus.CATERING_PICKUP.available = serviceModeStatus.CATERING_PICKUP.capable;
        serviceModeStatus.DELIVERY.available = serviceModeStatus.DELIVERY.capable;
        serviceModeStatus.EAT_IN.available = serviceModeStatus.EAT_IN.capable;
        serviceModeStatus.CURBSIDE.available =
          IS_AVAILABLE_CURBSIDE_SERVICE_MODE && serviceModeStatus.CURBSIDE.capable;

        serviceModeStatus.TAKEOUT.available = serviceModeStatus.TAKEOUT.capable;
        serviceModeStatus.DRIVE_THRU.available = serviceModeStatus.DRIVE_THRU.capable;
        serviceModeStatus.PICKUP_WINDOW.available = serviceModeStatus.PICKUP_WINDOW.capable;
      } else {
        serviceModeStatus.CATERING_DELIVERY.available = serviceModeStatus.CATERING_DELIVERY.capable;
        serviceModeStatus.CATERING_PICKUP.available = serviceModeStatus.CATERING_PICKUP.capable;
        serviceModeStatus.DELIVERY.available = serviceModeStatus.DELIVERY.capable && deliveryOpen;
        serviceModeStatus.EAT_IN.available = serviceModeStatus.EAT_IN.capable && diningRoomOpen;
        serviceModeStatus.CURBSIDE.available =
          IS_AVAILABLE_CURBSIDE_SERVICE_MODE && serviceModeStatus.CURBSIDE.capable && curbsideOpen;
        serviceModeStatus.TAKEOUT.available = serviceModeStatus.TAKEOUT.capable && diningRoomOpen;
        serviceModeStatus.DRIVE_THRU.available =
          serviceModeStatus.DRIVE_THRU.capable && driveThruOpen;
        serviceModeStatus.PICKUP_WINDOW.available =
          serviceModeStatus.PICKUP_WINDOW.capable && pickupWindowOpen;
      }

      return serviceModeStatus;
    },
    [
      isCateringDeliveryEnabled,
      isCateringEnabled,
      isCurbsideEnabled,
      isDeliveryEnabled,
      driveThruDisabled,
      dineInDisabled,
      takeOutDisabled,
      enableDeliveryOutsideOpeningHours,
      enableFutureOrdering,
    ]
  );

  return {
    generateServiceModeStatusForStore,
  };
};

const isAvailableAndNotDisabled = ({
  available,
  disabled,
}: ServiceModeStatus[keyof ServiceModeStatus]) => available && !disabled;

export const useServiceModeStatus = (
  store: StoreProxy | IRestaurantNode,
  ignoreFutureOrdering = false
) => {
  const restaurantsConfig = useConfigValue({ key: 'restaurants', defaultValue: {} });
  const validMobileOrderingEnvs = restaurantsConfig.validMobileOrderingEnvs ?? [];
  const { serviceMode } = useServiceModeContext();

  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const storeSelection2_0Enabled = useFlag(LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0);
  const isAlphaBetaStoreOrderingEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_ALPHA_BETA_STORE_ORDERING
  );

  const hasAvailableProperty = useGetRestaurantAvailabilityFn();
  const getRestaurant = useGetRestaurantFn();
  const { generateServiceModeStatusForStore } = useServiceModeStatusGenerator(ignoreFutureOrdering);

  const serviceModeStatus = useMemo(
    () => generateServiceModeStatusForStore(store),
    [generateServiceModeStatusForStore, store]
  );

  const allServiceModesUnavailable = Object.values(serviceModeStatus).every(
    ({ available, disabled }) => !available || disabled
  );

  const availablePickupServiceModes = useMemo(
    () => PICKUP_SERVICE_MODES.filter(value => isAvailableAndNotDisabled(serviceModeStatus[value])),
    [serviceModeStatus]
  );

  const isRestaurantAvailable = useCallback(
    async (restaurantData: StoreProxy | IRestaurantNode) => {
      const mobileOrderingEnabled = isMobileOrderingAvailable(
        restaurantData,
        isAlphaBetaStoreOrderingEnabled,
        validMobileOrderingEnvs
      );
      const { number: storeId } = restaurantData;

      if (!storeId) {
        return false;
      }

      const rbiRestaurant = await getRestaurant(storeId);

      const isRestaurantPosAvailable = Boolean(rbiRestaurant?.available);

      const open =
        !serviceMode ||
        [ServiceMode.DELIVERY, ServiceMode.CATERING_PICKUP, ServiceMode.CATERING_DELIVERY].includes(
          serviceMode
        ) ||
        (serviceModeStatus[serviceMode].available && !serviceModeStatus[serviceMode].disabled);

      return open && isRestaurantPosAvailable && mobileOrderingEnabled;
    },
    [serviceMode, serviceModeStatus, isAlphaBetaStoreOrderingEnabled] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Determines whether a restaurant can be selected */
  const restaurantCanBeSelected = useMemo(() => {
    // Store selection 2.0: any store can be selected
    if (storeSelection2_0Enabled && store._id) {
      return true;
    }

    if (!enableOrdering) {
      return false;
    }

    /*
     * Catering service mode is preselected and assumed to be available for
     * ordering during selection.
     */
    if (isCatering(serviceMode)) {
      return true;
    }

    // Store selection 1.0: store can only be selected if there is an available service mode
    return (
      Boolean(store.restaurantPosData) &&
      hasAvailableProperty(store) &&
      isMobileOrderingAvailable(store, isAlphaBetaStoreOrderingEnabled, validMobileOrderingEnvs) &&
      Boolean(availablePickupServiceModes.length)
    );
  }, [
    storeSelection2_0Enabled,
    store,
    enableOrdering,
    serviceMode,
    hasAvailableProperty,
    isAlphaBetaStoreOrderingEnabled,
    validMobileOrderingEnvs,
    availablePickupServiceModes.length,
  ]);

  return {
    allServiceModesUnavailable,
    availablePickupServiceModes,
    isRestaurantAvailable,
    restaurantCanBeSelected,
    serviceModeStatus,
  };
};
